import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BuildIcon from '@mui/icons-material/Build';
import { unitApi, ownerApi, invoiceApi, maintenanceApi } from '../../services/api';

interface DashboardSummary {
  totalUnits: number;
  totalOwners: number;
  outstandingReceivables: number;
  openMaintenanceRequests: number;
}

const SummaryCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  format?: 'number' | 'currency';
}> = ({ title, value, icon, color, format = 'number' }) => {
  const displayValue =
    format === 'currency'
      ? `$${(value as number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : typeof value === 'number'
      ? value.toLocaleString()
      : value;

  return (
    <Paper
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        height: '100%',
      }}
      elevation={2}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: color,
          color: '#fff',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {displayValue}
        </Typography>
      </Box>
    </Paper>
  );
};

const Dashboard: React.FC = () => {
  const [summary, setSummary] = useState<DashboardSummary>({
    totalUnits: 0,
    totalOwners: 0,
    outstandingReceivables: 0,
    openMaintenanceRequests: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const [unitsRes, ownersRes, invoicesRes, maintenanceRes] = await Promise.all([
          unitApi.getAll(),
          ownerApi.getAll(),
          invoiceApi.getAll(),
          maintenanceApi.getRequests(),
        ]);

        const units = Array.isArray(unitsRes.data) ? unitsRes.data : unitsRes.data?.data || [];
        const owners = Array.isArray(ownersRes.data) ? ownersRes.data : ownersRes.data?.data || [];
        const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : invoicesRes.data?.data || [];
        const maintenance = Array.isArray(maintenanceRes.data) ? maintenanceRes.data : maintenanceRes.data?.data || [];

        const outstanding = invoices
          .filter((inv: any) => inv.status !== 'Paid')
          .reduce((sum: number, inv: any) => sum + (inv.balanceDue || inv.totalAmount || 0), 0);

        const openRequests = maintenance.filter(
          (req: any) => req.status !== 'Completed' && req.status !== 'Cancelled'
        ).length;

        setSummary({
          totalUnits: units.length,
          totalOwners: owners.length,
          outstandingReceivables: outstanding,
          openMaintenanceRequests: openRequests,
        });
      } catch (err: any) {
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SummaryCard
            title="Total Units"
            value={summary.totalUnits}
            icon={<ApartmentIcon sx={{ fontSize: 28 }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SummaryCard
            title="Total Owners"
            value={summary.totalOwners}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            color="#388e3c"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SummaryCard
            title="Outstanding Receivables"
            value={summary.outstandingReceivables}
            icon={<ReceiptIcon sx={{ fontSize: 28 }} />}
            color="#f57c00"
            format="currency"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <SummaryCard
            title="Open Maintenance"
            value={summary.openMaintenanceRequests}
            icon={<BuildIcon sx={{ fontSize: 28 }} />}
            color="#d32f2f"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
